<template>
	<TypographyTitle :level="2">
		{{ $t('rover-change-tariff.header') }}
	</TypographyTitle>

	<Row>
		<Col span="24" offset="0" :md="{ span: 20 }" class="frame">
			<div class="frame-content">
				<ApplyTariffToListTable
					:rovers="wrappedRover"
					:loading="isLoadingTable"
				/>
				<TariffActionSelect v-model="tariffAction" class="pd-top-lg" />
				<div v-if="tariffAction === ACTION_CHANGE" class="change-tariff-alert">
					<WarningOutlined class="warning-icon" />
					{{ $t('tariffs.applyTariffModal.changeTariffAlert') }}
				</div>
				<div class="params-grid">
					<AddDaysSelect v-model="addDays" :action="tariffAction" />
					<span v-if="tariffAction === ACTION_CHANGE">{{ $t('tariffs.applyTariffModal.tariffNameLabel') }}</span>
					<Select
						v-if="tariffAction === ACTION_CHANGE"
						class="params-select"
						v-model:value="selectedTariffId"
						:options="tariffOptions"
						:loading="isLoadingTariffOptions"
					/>
				</div>
			</div>
			<div class="frame-action">
				<div class="total-price-wrapper">
					<span class="total-price-label">{{ $t('tariffs.applyTariffModal.totalPriceLabel') }}</span>
					<TypographyText strong class="total-price">{{ $t('tariffs.applyTariffModal.totalPriceValue', totalPrice) }}</TypographyText>
				</div>
				<Button
					key="submit"
					type="primary"
					:disabled="isSubmitting || isLoadingTable || isLoadingTariffOptions"
					:loading="isSubmitting"
					@click="onApplyTariff"
					>
					{{ $t('tariffs.tariffActions.' + tariffAction) }}
				</Button>
			</div>
		</Col>
	</Row>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue';
import { TypographyTitle, TypographyText, Select, Button, Row, Col } from 'ant-design-vue';
import { WarningOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';
import Utils from '@/api/Utils';

import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { showErrorMessage } from '@/components/common/errorMessage';
import { useTariffUpdateApi } from '@/components/tariffs/useTariffApplyToListApi';

import ApplyTariffToListTable from '@/components/tariffs/ApplyTariffToListTable';
import TariffActionSelect from '@/components/tariffs/TariffActionSelect';
import AddDaysSelect from '@/components/tariffs/AddDaysSelect';
import { ACTION_CHANGE, ACTION_EXTEND } from '@/components/tariffs/Consts';


export default {
	components: {
		TypographyTitle, TypographyText, Select, Button, Row, Col,
		ApplyTariffToListTable, TariffActionSelect, AddDaysSelect,
		WarningOutlined,
	},
	setup() {
		const { t } = useI18n();
		const route = useRoute();

		const rover = ref(null);
		const isLoadingTable = ref(true);
		const originalTariffId = ref(null);
		const selectedTariffId = ref(null);
		const tariffObjects = ref([]);
		const isLoadingTariffOptions = ref(true);
		onMounted(() => {
			Api.roverGetOne(route.params.roverId, response => {
				isLoadingTable.value = false;
				if (response.status === 'ok') {
					rover.value = response.rover;
					originalTariffId.value = response.rover.tariff && response.rover.tariff.base_tariff_id;
				} else {
					showErrorMessage(response.errors);
				}
			});
			Api.tariffGetList(response => {
				isLoadingTariffOptions.value = false;
				tariffObjects.value = response.tariffs;
			});
		});
		const wrappedRover = computed(() => (rover.value != null) ? [rover.value] : []);

		const tariffOptions = computed(() => tariffObjects.value.filter(
			tariff => tariff.id !== originalTariffId.value
		).map(tariff => ({
			value: tariff.id,
			label: tariff.title + ': ' + tariff.price_per_day + ' ' + t('tariffs.pricePerDayText', tariff.price_per_day),
		})));
		const tariffAction = ref(ACTION_EXTEND);
		watch(tariffAction, () => selectedTariffId.value = tariffOptions.value[0].value);
		const addDays = ref(null);
		const effectiveTariff = computed(() => tariffObjects.value.find(tariff => {
			return tariff.id === ((tariffAction.value === ACTION_CHANGE) ? selectedTariffId.value : originalTariffId.value);
		}));
		const totalPrice = computed(() => Utils.toRubles((effectiveTariff.value != null) ? (addDays.value ?? 0) * effectiveTariff.value.price_per_day : 0));
		const effectiveTariffId = computed(() => (effectiveTariff.value != null) ? effectiveTariff.value.id : 0);
		const roverIds = computed(() => [route.params.roverId]);
		const { isSubmitting, onApplyTariff } = useTariffUpdateApi(effectiveTariffId, addDays, roverIds, tariffAction);
		return {
			isLoadingTable,
			wrappedRover,
			tariffAction,
			addDays,
			selectedTariffId,
			tariffOptions,
			isLoadingTariffOptions,
			totalPrice,
			isSubmitting,
			onApplyTariff,
			ACTION_CHANGE,
		};
	},
}
</script>

<style scoped>
@import '@/assets/apply-tariff.css';

.frame {
	max-width: 750px;
	border-radius: 10px;
	border: 1px solid rgba(0, 21, 41, .2);
}

.frame-content,
.frame-action {
	padding: 24px;
}

.frame-action {
	background-color: rgba(0, 0, 0, .02);
	border-top: 1px solid rgba(0, 21, 41, .2);
	display: flex;
	align-items: center;
}

.params-select {
	max-width: 300px;
}

.total-price-wrapper {
	flex-grow: 1;
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
}

.total-price-label {
	margin-right: 12px;
	white-space: nowrap;
}

.total-price {
	font-size: 19px;
	white-space: nowrap;
}
</style>
